import { createReducer } from '@reduxjs/toolkit'

import * as authenticationActions from 'redux/actions/Authentication/actions'
import * as actions from 'redux/actions/DataPlatform/SIM/actions'
import * as standardModelActions from 'redux/actions/DataPlatform/StandardModels/actions'
import * as tableActions from 'redux/actions/DataPlatform/tables/v4/actions'

import NodeType from 'types/transformation/enums/NodeType'
import { FieldSourcesMap } from 'types/transformation/types/fieldSources'
import { InputNode, NodeData } from 'types/transformation/types/nodes'

export type TFetchStatus =
  | 'idle'
  | 'fetching'
  | 'success'
  | 'notFound'
  | 'error'

export type SIMTable = {
  id: string
  name: string
  alias: string | null
  technical_name: string
  source_tables: string[]
  // real_names of the fields whose MAP column has a mapped left column.
  modelled_fields: string[]
  // The source columns of each mapped field, keyed like modelled_fields.
  field_sources: FieldSourcesMap
}

export type LatestJobRun = {
  started_on: string | null
  completed_on: string | null
  state: string
}

export type SIM = {
  id: string
  name: string
  type: string
  latest_run: LatestJobRun | null
  tables: SIMTable[]
}

export interface SIMReducerType {
  data: SIM | null
  status: TFetchStatus
}

const INITIAL_STATE: SIMReducerType = {
  data: null,
  status: 'idle'
}

/**
 * Derive the source table ids of a saved SIM table from its INPUT nodes.
 * Mirrors the backend's getSourceTableIds so the overview list reflects a
 * newly modeled table without a full refetch.
 */
function getSourceTableIds(nodes: NodeData[]): string[] {
  return nodes
    .filter((node): node is InputNode => node.type === NodeType.INPUT)
    .map((node) => node.source_table_id)
    .filter((id): id is string => Boolean(id))
}

const simReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getSIM, (state) => {
      // Clear stale data so switching ontology type (custom <-> standard,
      // separate routes sharing this slice) shows a loading state instead of
      // the previous type's tables.
      state.data = null
      state.status = 'fetching'
    })
    .addCase(actions.getSIMSuccess, (state, action) => {
      state.data = action.payload
      state.status = 'success'
    })
    .addCase(actions.getSIMFailed, (state, action) => {
      state.status = action.payload.notFound ? 'notFound' : 'error'
    })
    .addCase(standardModelActions.activateStandardSuccess, (state, action) => {
      state.data = action.payload
      state.status = 'success'
    })
    .addCase(tableActions.putSIMTableSuccess, (state, action) => {
      if (!state.data) return

      const savedTable = state.data.tables.find(
        (table) => table.id === action.payload.id
      )

      if (savedTable) {
        savedTable.source_tables = getSourceTableIds(action.payload.nodes)
        savedTable.modelled_fields = action.payload.modelled_fields
        savedTable.field_sources = action.payload.field_sources
      }
    })
    .addCase(actions.deleteCustomSIMSuccess, (state) => {
      state.data = null
      state.status = 'notFound'
    })
    .addCase(actions.startSIMImportSuccess, (state, action) => {
      if (!state.data) return

      state.data.latest_run = action.payload
    })
    .addCase(actions.pollSIMImportSuccess, (state, action) => {
      if (!state.data) return

      state.data.latest_run = action.payload
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default simReducer
